import { Node, mergeAttributes } from '@tiptap/core';
import { getStyleHTMLAttributes, type StyleAttrs } from '../utils/styleHelper';

export interface CalloutOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    callout: {
      setCallout: (attributes?: { type?: string }) => ReturnType;
      toggleCallout: (attributes?: { type?: string }) => ReturnType;
      unsetCallout: () => ReturnType;
    };
  }
}

export const CalloutExtension = Node.create<CalloutOptions>({
  name: 'callout',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  content: 'block+',

  defining: true,

  isolating: true,

  addAttributes() {
    return {
      type: {
        default: 'info',
        parseHTML: (element) => {
          // Check data attribute first
          if (element.dataset.calloutType) {
            return element.dataset.calloutType;
          }
          // Check class names for type - including template-specific classes
          const classList = element.className || '';
          // Template-specific classes (check these first as they're more specific)
          if (classList.includes('callout-callout')) return 'callout';
          if (classList.includes('pro-tip')) return 'tip';
          if (classList.includes('table-of-contents')) return 'table-of-contents';
          if (classList.includes('key-takeaways')) return 'key-takeaways';
          if (classList.includes('expert-quote')) return 'expert-quote';
          if (classList.includes('citation')) return 'citation';
          if (classList.includes('quote')) return 'quote';
          // Standard callout classes
          if (classList.includes('warning')) return 'warning';
          if (classList.includes('success')) return 'success';
          if (classList.includes('error') || classList.includes('danger')) return 'error';
          if (classList.includes('tip')) return 'tip';
          return 'info';
        },
        renderHTML: (attributes) => ({
          'data-callout-type': attributes.type,
        }),
      },
      variant: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-callout-variant'),
        renderHTML: (attributes) => (
          attributes.variant ? { 'data-callout-variant': attributes.variant } : {}
        ),
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-callout-title'),
        renderHTML: (attributes) => (
          attributes.title ? { 'data-callout-title': attributes.title } : {}
        ),
      },
      attribution: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-callout-attribution'),
        renderHTML: (attributes) => (
          attributes.attribution ? { 'data-callout-attribution': attributes.attribution } : {}
        ),
      },
      stylePreset: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-callout-style-preset'),
        renderHTML: (attributes) => (
          attributes.stylePreset ? { 'data-callout-style-preset': attributes.stylePreset } : {}
        ),
      },
      style: {
        default: null,
        parseHTML: (element) => {
          // Parse inline style to object
          const styleString = element.getAttribute('style');
          if (!styleString) return null;

          const styleObj: Record<string, string> = {};
          styleString.split(';').forEach((rule) => {
            const [property, value] = rule.split(':').map((s) => s.trim());
            if (property && value) {
              // Convert kebab-case to camelCase
              const camelProperty = property.replace(/-([a-z])/g, (_, letter) =>
                letter.toUpperCase()
              );
              styleObj[camelProperty] = value;
            }
          });
          return Object.keys(styleObj).length > 0 ? styleObj : null;
        },
        renderHTML: () => ({}), // Style is rendered separately
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-callout]',
      },
      {
        tag: 'div.callout',
      },
      {
        tag: 'div.callout-box',
      },
      {
        tag: 'aside.callout',
      },
      {
        tag: 'div.alert',
      },
      {
        tag: 'div.notice',
      },
      // Template-specific callout classes
      {
        tag: 'div.pro-tip',
      },
      {
        tag: 'div.warning',
      },
      {
        tag: 'div.key-takeaways',
      },
      {
        tag: 'div.table-of-contents',
      },
      {
        tag: 'div.callout-table-of-contents',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const type = HTMLAttributes['data-callout-type'] || node.attrs.variant || node.attrs.type || 'info';
    const styleAttrs = getStyleHTMLAttributes(node.attrs as StyleAttrs);

    // Map callout types to both standard and template-specific CSS classes
    const templateClassMap: Record<string, string> = {
      callout: 'callout',
      tip: 'pro-tip',
      info: 'pro-tip',
      warning: 'warning',
      'table-of-contents': 'table-of-contents',
      'key-takeaways': 'key-takeaways',
      quote: 'quote',
      'expert-quote': 'quote',
      citation: 'quote',
      success: 'pro-tip', // Map success to pro-tip for template compatibility
      error: 'warning', // Map error to warning for template compatibility
    };
    const templateClass = templateClassMap[type] || 'callout';

    const attrs = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, styleAttrs, {
      'data-callout': '',
      class: `callout callout-${type} ${templateClass}`,
    });

    if (type === 'quote' || type === 'expert-quote' || type === 'citation') {
      const children: any[] = ['div', attrs];
      if (node.attrs.title) {
        children.push(['p', { style: 'margin: 0 0 1rem 0;' }, ['strong', ['em', node.attrs.title]]]);
      }
      children.push(0);
      if (node.attrs.attribution) {
        children.push(['p', { style: 'margin: 1rem 0 0 0;' }, ['em', node.attrs.attribution]]);
      }
      return children;
    }

    return ['div', attrs, 0];
  },

  addCommands() {
    return {
      setCallout:
        (attributes) =>
        ({ commands }) => {
          return commands.wrapIn(this.name, attributes);
        },
      toggleCallout:
        (attributes) =>
        ({ commands }) => {
          return commands.toggleWrap(this.name, attributes);
        },
      unsetCallout:
        () =>
        ({ commands }) => {
          return commands.lift(this.name);
        },
    };
  },
});

export default CalloutExtension;
